<template>
  <div autofocus>
    <AlertBox v-if="showPremiumAlert" @closeAlert="showPremiumAlert = false">
      <div class="premium-upgrade-box">
        <img src="@/assets/img/premium-icon.svg" alt="" />
        <h4 class="gradient-text">Pro feature only</h4>
        <p>Upgrade to PRO account to unlock more features and access to our full PRO library.</p>
        <RouterLink class="pricing-link" to="/pricing">View Plans</RouterLink>
      </div>
    </AlertBox>
    <div
      class="loading-spinner-overlay"
      id="loading-spinner-overlay"
      ref="loadingSpinnerRef"
      style="display: none"
    >
      <div class="loading-spinner"></div>
    </div>

    <MobileSearch
      :typewriterWords="flareSearchSuggestions"
      @showPremiumAlert="showPremiumAlert = true"
    />

    <div class="heading">
      <div class="icon">
        <img style="display: none" id="iconImage" ref="iconRef" />
      </div>
      <div class="focalLength">
        <select
          id="focalLength"
          name="focalLength"
          ref="focalLengthRef"
          style="display: none"
          @mousedown="handleFocalLengthClick"
          @change="setFocalLengthChange($event)"
        >
          <option v-for="option in store.focalLengthOptionsRight" :key="option" :value="option">
            {{ option }}
          </option>
        </select>
      </div>
      <div class="transmission">
        <select
          id="transmission"
          name="transmission"
          ref="transmissionRef"
          style="display: none"
          @mousedown="handleTransmissionClick"
          @change="setTransmissionChange($event)"
        >
          <option v-for="option in store.transmissionsRight" :key="option" :value="option">
            {{ option }}
          </option>
        </select>
      </div>
    </div>
    <div class="video-container" ref="videoContainerRef">
      <div class="overlay" @click="playVideo()"></div>
      <SaveBtn
        :status="saveBtnStatus"
        :loading="saveBtnLoading"
        @click="addWishlist"
        :viewCondition="showSaveIcon"
      />

      <div class="spinner-container" id="spinner" style="display: none" ref="spinnerRef">
        <div class="spinner-load"></div>
      </div>
      <video
        id="video"
        class="mob_video"
        playsinline
        oncontextmenu="return false;"
        preload="auto"
        ref="videoRef"
        @click="playVideo()"
        @ended="reloadPage()"
        disablePictureInPicture
        controlsList="nodownload noplaybackrate"
        @play="setVideoPlay"
        @pause="setVideoPause"
        @timeupdate="updateProgressBar"
      >
        <source src="" type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      <div class="video-controls video-control-mobile">
        <Transition name="fade">
          <div class="video-timeline-bar" v-show="showTimeline">
            <div class="video-timeline-progress" ref="progressBar"></div>
          </div>
        </Transition>
        <Transition name="fade">
          <div
            v-show="showFullscreen"
            class="video-control-option video-full-screen"
            @click.prevent="showFullScreen()"
          >
            <img src="@/assets/img/fullscreen.svg" width="12" class="fullscreen-img" />
          </div>
        </Transition>
      </div>
      <div
        class="play-button-img"
        id="playButton"
        ref="playButton"
        @click="playVideo()"
        v-if="!videoPlaying"
      >
        <img src="../../assets/img/play.png" />
      </div>
    </div>
    <div class="lenslist-container">
      <div class="lensesList">
        <ul
          class="viewport"
          ref="viewport"
          @scroll="() => store.snapToClosestOption(viewport, true)"
        >
          <li v-for="item in store.data" :key="item.id">
            <a class="nav-title nav-entity">{{ item.name }}</a>
            <ul class="nested-list" v-if="item.lenses && item.lenses.length">
              <li
                v-for="lens in uniqueLenses(item.lenses)"
                :key="getLinkId(lens.name)"
                class="nav-item nav-entity"
              >
                <a
                  :ref="el => setLabelRef(lens.name, el)"
                  @click="setSelection(lens.name)"
                  :id="getLinkId(lens.name)"
                  href="javascript:;"
                  class="nav-link"
                  :class="{ 'nav-link-premium': isLensLocked(lens.name) }"
                  :data-premium-locked="isLensLocked(lens.name)"
                  >{{ lens.name }}</a
                >
                <span
                  :ref="el => setBadgeRef(lens.name, el)"
                  class="badge-group"
                  :class="{ 'badge-group-premium': isLensLocked(lens.name) }"
                  v-if="isLensLocked(lens.name) || lens.isNew"
                >
                  <span class="new-badge" v-if="lens.isNew">NEW</span>
                  <img
                    v-if="isLensLocked(lens.name)"
                    :src="proLogo"
                    alt="PRO lens"
                    class="premium-lock-icon"
                    @click.stop="showPremiumAlert = true"
                    @load="schedulePositionBadges"
                  />
                </span>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </div>
    <div class="mobile-details-container">
      <DetailsContainer
        side="right"
        v-if="singleVideoDetail"
        :videoDetails="singleVideoDetail"
        @showDetails="handleDetailsShow"
        :showLensDetails="showLensDetails"
      />
    </div>
  </div>
</template>

<script setup>
import { onMounted, watch, ref, onUnmounted, computed, nextTick } from 'vue'
import { storeToRefs } from 'pinia'
import { useLensesStore } from '@/stores/Lenses'
import { useUsersStore } from '@/stores/Users'
import { usePremiumLock } from '@/composable/Premium.js'
import MobileSearch from './MobileSearch.vue'
import '../../styles/MobileComponents/MiddleContainer.css'
import { ampli } from '@/ampli'
import SaveBtn from '@/components/Selects/SaveBtn.vue'
import DetailsContainer from '../DetailsContainer.vue'
import AlertBox from '../AlertBox.vue'
import { useWishlistStore } from '@/stores/Wishlist'
import { addToWishlist, removeFromWishlist } from '@/api/wishlist'
import { useRouter } from 'vue-router'
import { flareSearchSuggestions } from '@/utils/constants'
import { saveLensToStorage, fallbackLens } from '@/includes/lens'
import proLogo from '@/assets/img/PRO_logo.webp'

// Store and ref declarations
const store = useLensesStore()
const userStore = useUsersStore()
const focalLengthRef = ref(null)
const transmissionRef = ref(null)
const iconRef = ref(null)
const spinnerRef = ref(null)
const videoRef = ref(null)
const loadingSpinnerRef = ref(null)
const playButton = ref(null)
const viewport = ref(null)
const videoPlaying = ref(false)
const videoContainerRef = ref(null)
const progressBar = ref(null)
const showTimeline = ref(false)
const showFullscreen = ref(true)
const showSaveIcon = ref(true)
const singleVideoDetail = ref(null)
const saveBtnStatus = ref(null)
const saveBtnLoading = ref(false)
const showLensDetails = ref(false)
const showPremiumAlert = ref(false)

const {
  handleTransmissionClick: _lockTransmission,
  handleFocalLengthClick: _lockFocalLength,
  guardSave,
  guardPlay
} = usePremiumLock({
  leftDetail: singleVideoDetail,
  isPremiumUser: computed(() => store.isPremiumUser),
  onLocked: () => {
    showPremiumAlert.value = true
  }
})

const handleTransmissionClick = event => _lockTransmission(event)
const handleFocalLengthClick = event => _lockFocalLength(event)

const { SelectedVideo, SelectedFocalLength, SelectedTransmission } = storeToRefs(store)

const wishlistStore = useWishlistStore()

const savedVideos = computed(() => wishlistStore.wishlist)

function showFullScreen() {
  store.enterFullscreen(videoRef)
}

const router = useRouter()

let currentPathObject = router.currentRoute.value

// Positions each lens's NEW/PRO badge-group right where its (possibly wrapped)
// label text actually ends, measured via the Range API rather than relying on
// pure CSS inline flow, since the sidebar's `overflow-x: hidden` silently clips
// anything that doesn't fit rather than reflowing it.
const labelRefs = {}
const badgeRefs = {}

function setLabelRef(name, el) {
  if (el) labelRefs[name] = el
  else delete labelRefs[name]
}
function setBadgeRef(name, el) {
  if (el) badgeRefs[name] = el
  else delete badgeRefs[name]
}

function positionBadge(labelEl, badgeEl) {
  const item = labelEl.closest('.nav-item')
  if (!item) return

  const GAP = 6
  const badgeWidth = badgeEl.offsetWidth

  // Reserve trailing space on the label's last wrapped line for the badge -
  // for a `display: inline` element, right padding only applies to its final
  // line fragment, so this makes the text itself wrap a word earlier when
  // needed instead of orphaning the badge on a line of its own
  labelEl.style.paddingRight = `${badgeWidth + GAP}px`

  const range = document.createRange()
  range.selectNodeContents(labelEl)
  const rects = range.getClientRects()
  if (!rects.length) return

  const lastRect = rects[rects.length - 1]
  const itemRect = item.getBoundingClientRect()

  let left = lastRect.right - itemRect.left + GAP
  let top = lastRect.top - itemRect.top

  // Only possible if the badge alone is wider than the whole row - no amount
  // of reserved space helps, so fall back to dropping it below
  const wraps = badgeWidth + GAP > itemRect.width
  if (wraps) {
    left = 0
    top = lastRect.bottom - itemRect.top + 4
  }

  badgeEl.style.left = `${left}px`
  badgeEl.style.top = `${top}px`
  item.style.paddingBottom = wraps ? `${badgeEl.offsetHeight + 4}px` : ''
}

let positionFrame = null
function schedulePositionBadges() {
  if (positionFrame) cancelAnimationFrame(positionFrame)
  positionFrame = requestAnimationFrame(() => {
    Object.keys(badgeRefs).forEach(name => {
      const labelEl = labelRefs[name]
      const badgeEl = badgeRefs[name]
      if (labelEl && badgeEl) positionBadge(labelEl, badgeEl)
    })
  })
}

let resizeObserver = null

onMounted(async () => {
  await store.fetchData()

  // Get default lens from localStorage or use the environment variable
  let defaultLens = store.SelectedVideo || store.defaultMobileVideoId
  // Try to set the selected video
  store.setSelectedVideo(defaultLens)

  // Check if the selected lens exists
  const selectedItem = document.getElementById(defaultLens)
  if (!selectedItem) {
    console.error('Default lens not found, setting the first lens as default')
    const fallbackLensId = fallbackLens(null, store.data)
    if (fallbackLensId) {
      defaultLens = fallbackLensId
      store.setSelectedVideo(defaultLens)

      // Save the fallback lens to localStorage
      saveLensToStorage(defaultLens, null)
    }
  }

  // Get the final lens element after possible fallback
  const finalLensItem = document.getElementById(defaultLens)
  if (finalLensItem) {
    finalLensItem.parentElement.classList.add('active-li')
  }

  store.addHeightOntopAndBottom(viewport)

  await nextTick()
  if (defaultLens) {
    const listItem = document.getElementById(defaultLens)
    if (listItem) {
      setTimeout(() => {
        store.scrollToItem(listItem)
      }, 100)
    }
  }

  schedulePositionBadges()
  resizeObserver = new ResizeObserver(schedulePositionBadges)
  if (viewport.value) resizeObserver.observe(viewport.value)
})

onUnmounted(() => {
  if (resizeObserver) resizeObserver.disconnect()
  if (positionFrame) cancelAnimationFrame(positionFrame)
})

watch(
  () => store.data,
  () => nextTick(schedulePositionBadges)
)

function getLinkId(name) {
  // Convert to lowercase and replace spaces with hyphens
  return name.toLowerCase().replace(/\s+/g, '-')
}

function isLensLocked(name) {
  return userStore.isLoggedInValue && !store.isPremiumUser && store.isLensPremium(name)
}

function setSelection(lens) {
  if (isLensLocked(lens)) {
    showPremiumAlert.value = true
  }
  store.setSelectedVideo(lens.replace(/\s+/g, '-').toLowerCase())
}

const uniqueLenses = lensCategory => {
  if (lensCategory) {
    const uniqueLenses = {}

    // Iterate through lenses in the category
    lensCategory.forEach(lens => {
      const name = lens.name
      const isNew = lens.isNew

      // If lens name is not already in uniqueLenses or isNew
      if (!uniqueLenses[name] || isNew) {
        uniqueLenses[name] = isNew
      }
    })

    // Convert uniqueLenses object to an array of objects
    const uniqueLensArray = Object.keys(uniqueLenses).map(name => ({
      name,
      isNew: uniqueLenses[name]
    }))

    // Free lenses first, then premium lenses, each group alphabetical
    uniqueLensArray.sort((a, b) => {
      const aPremium = store.isLensPremium(a.name)
      const bPremium = store.isLensPremium(b.name)
      if (aPremium !== bPremium) return aPremium ? 1 : -1
      return a.name.localeCompare(b.name)
    })

    return uniqueLensArray
  }
  return []
}

function setFocalLengthChange(event) {
  const selected = event.target.value
  store.setSelectedFocalLength(selected)
}
function setTransmissionChange(event) {
  const selected = event.target.value
  store.setSelectedTransmission(selected)
}

async function reloadTransmission() {
  store.SelectedVideoDetails.sort((a, b) => {
    const focalLengthA = a.focalLength ? parseFloat(a.focalLength.replace('mm', '')) : 0
    const focalLengthB = b.focalLength ? parseFloat(b.focalLength.replace('mm', '')) : 0
    const transmissionA = a.transmission
      ? parseFloat(a.transmission.toString().replace(/[^0-9.]/g, ''))
      : 0
    const transmissionB = b.transmission
      ? parseFloat(b.transmission.toString().replace(/[^0-9.]/g, ''))
      : 0
    const focalDiff = focalLengthA - focalLengthB
    if (focalDiff !== 0) {
      return focalDiff
    }
    return transmissionA - transmissionB
  })
  const transmissonOptions = store.SelectedVideoDetails.filter(
    s => s.focalLength === store.SelectedFocalLength
  )

  // Update the reactive array that already drives the template's <option v-for> -
  // rebuilding the <option> elements by hand here fought Vue's own patching of
  // the same <select> (that's what caused the insertBefore/patchProp crashes)
  store.transmissionsRight = transmissonOptions.map(option => option.transmission)
  store.setSelectedTransmission(transmissonOptions[0]?.transmission)

  // Wait for Vue to render the <option> elements from the array above before
  // setting .value - otherwise the select still has no options
  await nextTick()
  if (transmissionRef.value) {
    transmissionRef.value.style.display = 'block'
  }
}

/*
AMPLITUDE: Track lens watched
Triggers when video is played.
*/
const trackLensesWatched = () => {
  // const selectedLensRight = store.RightSelectedVideoDetails.find(
  //   (s) =>
  //     s.focalLength === store.RightFocalLength &&
  //     s.transmission === store.RightTransmission
  // );
  const selectedLens = store.SelectedVideoDetails.find(
    s =>
      s.focalLength === store.SelectedFocalLength && s.transmission === store.SelectedTransmission
  )

  ampli.lensVideoPlayed({
    leftLensId: selectedLens?.id,
    leftLensName: selectedLens?.name,
    leftLensCategoryId: selectedLens?.lensCategoryId,
    leftFocalLength: selectedLens?.focalLength,
    leftTransmission: selectedLens?.transmission
  })
}

const setVideoPlay = () => {
  videoPlaying.value = true
  showTimeline.value = true
  showFullscreen.value = true

  if (videoControlsTIimeout) clearTimeout(videoControlsTIimeout)

  videoControlsTIimeout = setTimeout(() => {
    showFullscreen.value = false
    showTimeline.value = false
    showSaveIcon.value = false
  }, 3000)
}
const setVideoPause = () => {
  videoPlaying.value = false
  showTimeline.value = true
  showFullscreen.value = true
  showSaveIcon.value = true

  clearTimeout(videoControlsTIimeout)
}

let videoControlsTIimeout

function playVideo() {
  const video = document.getElementById('video')

  // Check if the video is paused or ended before playing
  if (video.paused || video.ended) {
    if (guardPlay()) return
    video.play()
    trackLensesWatched()
  } else {
    // Pause the video if it's currently playing
    video.pause()
  }
}

function reloadPage() {
  const video1 = document.getElementById('video')
  video1.load()
  videoPlaying.value = false
}

// Progress bar on custom timeline
const updateProgressBar = e => {
  const { currentTime, duration } = e.target
  const percetageUpdate = (currentTime / duration) * 100
  progressBar.value.style.width = `${percetageUpdate}%`
}

const resetVideoControls = () => {
  showTimeline.value = false
  clearTimeout(videoControlsTIimeout)

  showFullscreen.value = true
  showSaveIcon.value = true
  progressBar.value.style.width = `0%`
}

const handleUnsave = async lensId => {
  const wishlistData = savedVideos.value.find(
    wishlist => wishlist.lensId === lensId && !wishlist.sceneImageUrl
  )

  await removeFromWishlist(wishlistData.id)

  wishlistStore.removeFromWishlist(wishlistData.id)

  saveBtnLoading.value = false
}

const addWishlist = async () => {
  if (guardSave()) return

  let videoObject
  let status
  let lensCategoryName

  videoObject = singleVideoDetail.value
  saveBtnLoading.value = false
  status = saveBtnStatus.value

  lensCategoryName = store.data.find(
    lens => lens.id === singleVideoDetail.value?.lensCategoryId
  )?.name

  const {
    id: lensId,
    name,
    focalLength,
    transmission,
    preview,
    flareImage,
    videoPath
  } = videoObject

  saveBtnLoading.value = true
  if (status === 'saved') {
    handleUnsave(lensId)
    return
  }

  const payload = {
    lensId,
    name,
    focalLength,
    transmission,
    flareImage: flareImage ? flareImage : preview,
    videoPath,
    categoryName: lensCategoryName
  }

  /*
    AMPLITUDE: Track lens saved
    Triggers when a lens save button is clicked
  */
  ampli.lensSaved({
    lensId: lensId,
    lensName: name,
    focalLength: focalLength,
    transmission: transmission,
    page: currentPathObject?.fullPath
  })

  const response = await addToWishlist(payload)

  if (response.status === 200) {
    wishlistStore.wishlist = [...wishlistStore.wishlist, response.data]
  }

  saveBtnLoading.value = false
}

const generateSaveIcon = () => {
  if (!singleVideoDetail.value) return
  const { id } = singleVideoDetail.value

  let idArray = []

  savedVideos.value?.forEach(obj => {
    if (!obj.sceneImageUrl) idArray.push(obj.lensId)
  })

  if (idArray.includes(id)) {
    saveBtnStatus.value = 'saved'
  } else {
    saveBtnStatus.value = 'unsaved'
  }
}

const handleDetailsShow = () => {
  // Locked only for a premium lens viewed by a non-premium user - free lenses
  // are always viewable regardless of the viewer's own plan.
  const isLocked = singleVideoDetail.value?.isPremium === true && !store.isPremiumUser
  if (isLocked) {
    showPremiumAlert.value = true
  } else {
    showLensDetails.value = !showLensDetails.value
  }
}

watch(savedVideos, () => {
  generateSaveIcon()
})

watch(singleVideoDetail, () => {
  generateSaveIcon()

  const lensCategoryName = store.data.find(
    lens => lens.id === singleVideoDetail.value?.lensCategoryId
  )?.name

  if (lensCategoryName) {
    singleVideoDetail.value.lensCategoryName = lensCategoryName
  }
})

watch(SelectedVideo, () => {
  store.setSelectedVideoDetails()
  reloadPage()

  const lensElementId = store.SelectedVideo.toLowerCase().replace(/\s+/g, '-')

  const listItem = document.getElementById(lensElementId)

  if (store.searchIsActive) {
    const navEntities = document.querySelectorAll('.nav-entity')
    navEntities.forEach(item => {
      item.style.transform = 'rotateX(0deg)'
    })
  }

  const lensesItem = store.SelectedVideoDetails.find(
    s =>
      s.focalLength === store.SelectedFocalLength && s.transmission === store.SelectedTransmission
  )
  if (lensesItem) {
    store.setVideoPathOnChange(lensesItem, videoRef)
    singleVideoDetail.value = lensesItem
    store.generateIcon(lensesItem.icon, iconRef)
  }

  store.scrollToItem(listItem)
  store.generateDropDown(' ', focalLengthRef, transmissionRef)
  const hasValidFocal = store.SelectedVideoDetails.some(
    s => s?.focalLength === store.SelectedFocalLength
  )
  if (!hasValidFocal) {
    store.setSelectedFocalLength(store.SelectedVideoDetails[0]?.focalLength)
  }
  const hasValidTransmission = store.SelectedVideoDetails.some(
    s =>
      s?.focalLength === store.SelectedFocalLength && s?.transmission === store.SelectedTransmission
  )
  if (!hasValidTransmission) {
    store.setSelectedTransmission(store.SelectedVideoDetails[0]?.transmission)
  }
  resetVideoControls()

  saveLensToStorage(lensElementId, null)
  store.setDefaultMobileVideo()
})

watch(SelectedFocalLength, () => {
  reloadPage()
  store.setSelectedVideoDetails()
  reloadTransmission()
  const lensesItem = store.SelectedVideoDetails.find(
    s =>
      s.focalLength === store.SelectedFocalLength && s.transmission === store.SelectedTransmission
  )

  singleVideoDetail.value = lensesItem
  store.generateIcon(lensesItem.icon, iconRef)
  store.setVideoPathOnChange(lensesItem, videoRef)
  resetVideoControls()
})

watch(SelectedTransmission, () => {
  reloadPage()

  store.setSelectedVideoDetails()

  const lensesItem = store.SelectedVideoDetails.find(
    s =>
      s.focalLength === store.SelectedFocalLength && s.transmission === store.SelectedTransmission
  )
  store.generateIcon(lensesItem.icon, iconRef)
  store.setVideoPathOnChange(lensesItem, videoRef)
  singleVideoDetail.value = lensesItem
  resetVideoControls()
})
</script>

<style scoped>
.video-control-mobile {
  bottom: 15px;
  padding: 0 10px;
  z-index: 3;
  width: 100%;
  flex-direction: column-reverse;
  align-items: end;
  justify-content: space-between;
  gap: 5px;
  min-height: 34px;
}

.video-control-mobile img {
  width: 22px;
  height: 26px;
  padding: 8px 0 4px 8px;
}

.lensesList {
  margin-right: 100px;
}

.fullscreen-img {
  opacity: 0.5;
}

.video-full-screen {
  position: absolute;
  top: 0;
}

@media screen and (max-width: 640px) {
  .lensesList {
    margin-left: 0;
    margin-right: 20px;
  }
}

.lenslist-container {
  display: flex;
  justify-content: center;
}

.video-timeline-bar {
  width: 100%;
  height: 2px;
  background: rgba(255, 255, 255, 0.1);
  position: relative;
  border-radius: 10px;
}

.video-timeline-progress {
  position: absolute;
  left: 0;
  width: 0%;
  height: 100%;
  background: rgba(128, 128, 128, 0.5);
  border-radius: 10px;
}

video::-webkit-media-controls-play-button,
video::-webkit-media-controls-pause-button {
  display: block !important;
}

.nav-title {
  margin-top: 0;
}

.mobile-details-container {
  margin: 0 auto;
  padding: 0 20px;
  max-width: 400px;
}

.nav-link-premium {
  opacity: 0.3;
}

.active-li .nav-link-premium {
  opacity: 0.6;
}

.badge-group-premium {
  opacity: 0.3;
}

.active-li .badge-group-premium {
  opacity: 0.6;
}

.nav-item {
  position: relative;
}

.nav-link {
  display: inline;
  white-space: normal;
  overflow-wrap: anywhere;
}

.badge-group {
  position: absolute;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 0.3rem;
  /* left/top are set from JS once the label's actual end position is measured */
}

.premium-lock-icon {
  display: inline-block;
  height: 0.85rem;
  width: auto;
  flex-shrink: 0;
  vertical-align: middle;
  cursor: pointer;
  opacity: 0.85;
}

.new-badge {
  display: inline-block;
  background-color: #0f52ba;
  color: #fff;
  padding: 0.2rem 0.5rem;
  border-radius: 0.3rem;
  font-size: 0.7rem;
  margin-left: 0.5rem;
}

.badge-group .new-badge {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  height: 0.85rem;
  margin-left: 0;
  padding: 0 0.3rem;
  font-size: 0.55rem;
  line-height: 1;
  box-sizing: border-box;
}
</style>
